import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import ConfirmModal from '../components/ConfirmModal';
import {
  getCancelReasons,
  createCancelReason,
  updateCancelReason,
  deleteCancelReason,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const MAX_REASON_LENGTH = 200;

/** Capitalize first letter of the string and first letter after each full stop (.). */
const formatReasonCaps = (text: string): string => {
  if (!text.trim()) return text;
  let out = text.charAt(0).toUpperCase() + text.slice(1);
  out = out.replace(/(\.\s*)(.)/g, (_, afterPeriod, char) => afterPeriod + char.toUpperCase());
  return out;
};

/** If more than 3 words and does not end with a full stop, add a full stop at the end (within max length). */
const ensureTrailingFullStop = (text: string, maxLength: number): string => {
  const trimmed = text.trim().slice(0, maxLength);
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= 3 || trimmed.endsWith('.')) return trimmed;
  const withoutTrailingSpaces = trimmed.trimEnd();
  if (withoutTrailingSpaces.length < maxLength) return withoutTrailingSpaces + '.';
  return withoutTrailingSpaces.slice(0, -1) + '.';
};

export interface CancelReasonItem {
  _id: string;
  metadata: {
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    tenantId?: string | null;
    reason: string;
  };
}

interface CancelReasonManagementProps {
  skipLayout?: boolean;
}

const CancelReasonManagement: React.FC<CancelReasonManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission } = useAuth();
  const [reasons, setReasons] = useState<CancelReasonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reasonToDelete, setReasonToDelete] = useState<{ id: string; name: string } | null>(null);
  const [editingReason, setEditingReason] = useState<CancelReasonItem | null>(null);
  const [formData, setFormData] = useState({ reason: '' });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ reason?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const reasonTextareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustReasonTextareaHeight = useCallback(() => {
    const ta = reasonTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const maxHeight = 200;
    ta.style.height = `${Math.min(ta.scrollHeight, maxHeight)}px`;
  }, []);

  const fetchReasons = useCallback(async (page: number, size: number = pageSize, search?: string) => {
    try {
      setLoading(true);
      const response = await getCancelReasons(page, size, search);
      setReasons(response.data.data || []);
      const pagination = response.data.pagination || {};
      setCurrentPage(pagination.currentPage || page);
      setTotalItems(pagination.totalItems || 0);
      setHasNextPage(pagination.hasNextPage || false);
      setHasPreviousPage(pagination.hasPreviousPage || false);
    } catch (err: any) {
      setError('Failed to load cancel reasons');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchReasons(1, pageSize, debouncedSearchTerm?.trim() || undefined);
  }, [debouncedSearchTerm, fetchReasons, pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!showModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal]);

  useEffect(() => {
    if (showModal) adjustReasonTextareaHeight();
  }, [showModal, formData.reason, adjustReasonTextareaHeight]);

  const handleOpenModal = (reason?: CancelReasonItem) => {
    if (reason) {
      setEditingReason(reason);
      setFormData({
        reason: formatReasonCaps((reason.spec?.reason ?? (reason as any).metadata?.reason ?? '').slice(0, MAX_REASON_LENGTH)),
      });
    } else {
      setEditingReason(null);
      setFormData({ reason: '' });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    if (!formData.reason.trim()) {
      setValidationErrors({ reason: 'Reason text is required' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    const reasonText = ensureTrailingFullStop(
      formData.reason.trim().slice(0, MAX_REASON_LENGTH),
      MAX_REASON_LENGTH
    );
    try {
      if (editingReason) {
        await updateCancelReason(editingReason._id, {
          spec: { reason: reasonText },
        });
      } else {
        await createCancelReason({
          spec: { reason: reasonText },
        });
      }
      setShowModal(false);
      fetchReasons(currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id: string, name: string) => {
    setReasonToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!reasonToDelete) return;
    try {
      await deleteCancelReason(reasonToDelete.id);
      const nextPage = currentPage > 1 && reasons.length === 1 ? currentPage - 1 : currentPage;
      fetchReasons(nextPage);
      setShowDeleteConfirm(false);
      setReasonToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
      setShowDeleteConfirm(false);
      setReasonToDelete(null);
    }
  };

  const handleRefresh = () => {
    fetchReasons(currentPage, pageSize);
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search cancel reasons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('cancelreason.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Reason
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading cancel reasons...</div>
        ) : reasons.length === 0 ? (
          <div className="empty-state">
            <h3>No cancel reasons yet</h3>
            <p>Add predefined reasons that can be selected when cancelling a booking</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 450px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Reason</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reasons.map((reason) => {
                    const full = reason.spec?.reason ?? (reason as any).metadata?.reason ?? '';
                    const maxLen = 140;
                    const truncated = full.length > maxLen ? `${full.slice(0, maxLen).trimEnd()}…` : full;
                    return (
                      <tr key={reason._id}>
                        <td>
                          <strong
                            title={full}
                            style={{ cursor: full.length > maxLen ? 'pointer' : 'default' }}
                          >
                            {truncated}
                          </strong>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {formatDateDDMMYYYY(reason.metadata?.createdAt ?? 0)}
                        </td>
                        <td>
                          <div className="action-buttons">
                            {hasPermission('cancelreason.update') && (
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleOpenModal(reason)}
                              >
                                Edit
                              </button>
                            )}
                            {hasPermission('cancelreason.delete') && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() =>
                                  handleDelete(
                                    reason._id,
                                    reason.spec?.reason ?? (reason as any).metadata?.reason ?? ''
                                  )
                                }
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      fetchReasons(1, newSize);
                    }}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100, 500].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && fetchReasons(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && fetchReasons(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {editingReason ? 'Edit Cancel Reason' : 'Add Cancel Reason'}
              </h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}
                <div className="form-group">
                  <label>
                    Reason text
                    <span className="required-star">*</span>
                  </label>
                  <textarea
                    ref={reasonTextareaRef}
                    value={formData.reason}
                    maxLength={MAX_REASON_LENGTH}
                    onChange={(e) => {
                      const raw = e.target.value.slice(0, MAX_REASON_LENGTH);
                      const formatted = formatReasonCaps(raw);
                      setFormData((prev) => ({ ...prev, reason: formatted }));
                    }}
                    onInput={adjustReasonTextareaHeight}
                    placeholder="e.g. Patient no longer needs transport"
                    className={validationErrors.reason ? 'input-error' : ''}
                    rows={2}
                    style={{ resize: 'none', minHeight: '60px' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap', gap: '4px' }}>
                    <span>
                      {validationErrors.reason && (
                        <small style={{ color: '#e74c3c', fontSize: '14px' }}>
                          {validationErrors.reason}
                        </small>
                      )}
                    </span>
                    <span className="helper-text" style={{ marginLeft: 'auto' }}>
                      {formData.reason.length}/{MAX_REASON_LENGTH}
                    </span>
                  </div>
                </div>
              </div>
              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingReason ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    editingReason ? 'Update' : 'Add'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Cancel Reason"
        message={`Are you sure you want to delete "${reasonToDelete?.name}"?`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setReasonToDelete(null);
        }}
      />
    </>
  );
};

export default CancelReasonManagement;
